import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { FileInput } from '../ui/FileInput';
import { Label } from '../ui/Label';
import { Alert } from '../ui/Alert';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetTrigger } from '../ui/Sheet';
import { apiFetch } from '../../lib/api';
import { ApiError, type Template } from '../../types/api';
import { CopyImagesPanel } from './CopyImagesPanel';
import { HtmlCheckReport } from './HtmlCheckReport';
import { HtmlPreview } from './HtmlPreview';
import { useHtmlCheck } from './useHtmlCheck';
import { useRehostImages } from './useRehostImages';

/** Above this the pasted text is not echoed into the text box (a 4 MB file would freeze it). */
const ECHO_LIMIT_CHARS = 200_000;

function nameFromFile(fileName: string): string {
  return fileName.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ');
}

/**
 * "Import HTML": upload a finished newsletter (.html) or paste its HTML,
 * see at once what was translated and what cannot be (each problem named),
 * look at it, and save it as a template. Nothing is stored until Save, and
 * the server checks the HTML again then.
 */
export function HtmlImportSheet() {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('');
  const [fileInfo, setFileInfo] = useState<{ name: string; chars: number } | null>(null);
  const [name, setName] = useState('');
  const [readError, setReadError] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const hasSource = source.trim() !== '';
  const check = useHtmlCheck(source, open && hasSource);
  const report = hasSource ? check.report : null;

  // The copied images arrive as new HTML: it becomes the working text (and is checked again).
  const onRehosted = useCallback((html: string) => {
    setSource(html);
    setFileInfo((info) => (info ? { ...info, chars: html.length } : info));
  }, []);
  const rehost = useRehostImages(source, onRehosted);
  const copying = rehost.run.status === 'running';

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ template: Template }>('/templates', {
        method: 'POST',
        // No blockJson: that is what makes it an HTML template.
        body: { name: name.trim(), html: report?.html || source },
      }),
    onSuccess: ({ template }) => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      setOpen(false);
      navigate(`/skabeloner/${template.id}`);
    },
  });

  function reset() {
    setSource('');
    setFileInfo(null);
    setName('');
    setReadError(false);
    rehost.reset();
    saveMutation.reset();
    if (fileInput.current) fileInput.current.value = '';
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setReadError(false);
    rehost.reset();
    try {
      const text = await file.text();
      setSource(text);
      setFileInfo({ name: file.name, chars: text.length });
      setName((current) => current || nameFromFile(file.name));
    } catch {
      setReadError(true);
    }
  }

  const echoed = fileInfo !== null && fileInfo.chars > ECHO_LIMIT_CHARS;
  const canSave =
    report !== null && report.valid && !check.pending && !copying && name.trim() !== '';
  const saveError =
    saveMutation.error instanceof ApiError && saveMutation.error.body.message
      ? saveMutation.error.body.message
      : saveMutation.isError
        ? 'The template could not be saved. Check the problems above.'
        : null;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <SheetTrigger asChild>
        <Button variant="secondary">Import HTML</Button>
      </SheetTrigger>
      <SheetContent
        title="Import an HTML newsletter"
        description="Upload a finished newsletter or paste its HTML. Mailchimp merge tags are translated. Nothing is saved until you choose Save."
        className="w-[760px]"
      >
        <form
          className="flex min-h-0 flex-grow flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) saveMutation.mutate();
          }}
        >
          <SheetBody>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-file">HTML file</Label>
              <FileInput
                id="import-file"
                ref={fileInput}
                accept=".html,.htm,text/html"
                onChange={(e) => void onFile(e)}
                disabled={copying}
              />
              {readError ? <Alert>The file could not be read.</Alert> : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-paste">Or paste the HTML</Label>
              <textarea
                id="import-paste"
                value={echoed ? '' : source}
                disabled={echoed || copying}
                placeholder={
                  echoed
                    ? `${fileInfo?.name} is loaded (too long to show here).`
                    : '<!doctype html> …'
                }
                spellCheck={false}
                wrap="off"
                onChange={(e) => {
                  setSource(e.target.value);
                  setFileInfo(null);
                  rehost.reset();
                }}
                className="h-32 w-full resize-y whitespace-pre rounded-[9px] border border-zinc-300 bg-card p-3 font-mono text-[12.5px] leading-5 text-text"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-name">Template name</Label>
              <Input
                id="import-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {report ? (
              <>
                <HtmlCheckReport
                  report={report}
                  pending={check.pending}
                  imageAction={
                    <CopyImagesPanel
                      images={report.images}
                      run={rehost.run}
                      onCopy={() => void rehost.start()}
                      disabled={check.pending}
                    />
                  }
                />
                <div className="flex flex-col gap-1.5">
                  <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-muted">
                    Preview (sample values for merge fields, links are not clickable)
                  </h3>
                  <HtmlPreview html={report.previewHtml} className="h-[420px]" />
                </div>
              </>
            ) : check.isError && hasSource ? (
              <Alert>The HTML could not be checked. Try again shortly.</Alert>
            ) : null}
            {saveError ? <Alert>{saveError}</Alert> : null}
          </SheetBody>
          <SheetFooter>
            <Button type="submit" disabled={!canSave || saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save as template'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
